import { observer } from 'mobx-react-lite';
import React, { useContext, useMemo } from 'react';
import { getAccountActivityRuleErrors } from '../../../../components/InputField/helper';
import { DialogContext } from '../../../../store/context/DialogContext';
import ButtonGroupNew from '../BusinessLiteForms/components/ButtonGroupNew';
import DocumentFirstStepHead from './components/DocumentFirstStepHead';
import { VARIANT } from '../../../../util/variant';
import BusinessV2FieldGrid from './BusinessV2FieldGrid';
import ExpectedActivityTableV2 from './ExpectedActivityTableV2';
import { collectVisibleFieldErrors, getFirstErrorInFieldOrder, getVisibleWizardFieldOrder, mapRuleErrorsToFieldErrors, mergeClearedFieldErrors, omitFieldPathErrors } from './helperV2';

const STACKED_CHECKBOX_FIELDS = new Set([
    "Deposit_Reasons",
]);

const PRIME_ACTIVITY_NARRATIVE_FIELDS = [
    "Primary_Business_Activities",
    "Business_Product_Services",
    "productsOrServices",
    "businessPurpose",
    "primaryCustomer",
    "revenueRange",
    "hasAuditStatements",
    "hasAnnualStatements",
    "hasFinancialForecast",
    "hasBusinessPlan",
    "businessInvolvements",
    "Illegal_Immoral_Disclosure",
];

const PRIME_ACTIVITY_SUPPORTING_FIELDS = [
    "Deposit_Reasons",
    "Payment_Reasons",
    "Deposit_Locations",
    "Payment_Locations",
];

const PRIME_ACTIVITY_COUNTRY_FIELDS = [
    "internationalCustomers",
];

const PRIME_ACTIVITY_CONVERSION_FIELDS = [
    "hasCoinConversionPlan",
    "hasCryptoConversionPlan",
];

const PRIME_ACTIVITY_TABLE_FIELDS = [
    "Amount_Of_Initial_Deposit",
    "Average_Balance",
    "Number_Of_Monthly_Ach_Deposits",
    "Amount_Of_Monthly_Ach_Deposits",
    "Number_Of_Monthly_Domestic_Wire_Deposits",
    "Amount_Of_Monthly_Domestic_Wire_Deposits",
    "Number_Of_Monthly_International_Wire_Deposits",
    "Amount_Of_Monthly_International_Wire_Deposits",
    "Number_Of_Monthly_Ach_Payments",
    "Amount_Of_Monthly_Ach_Payments",
    "Number_Of_Monthly_Domestic_Wire_Payments",
    "Amount_Of_Monthly_Domestic_Wire_Payments",
    "Number_Of_Monthly_International_Wire_Payments",
    "Amount_Of_Monthly_International_Wire_Payments",
    "Expected_Amount_Monthly_Digital_Currency_Custody",
    "Count_Of_Monthly_Stablecoin_Conversions_Deposits",
    "Expected_Amount_Monthly_Incoming_USDC",
    "Count_Of_Monthly_Stablecoin_Conversions_Payments",
    "Expected_Amount_Monthly_Outgoing_USDC",
    "Count_Of_Monthly_Cryptocurrency_Conversions",
    "Amount_Of_Monthly_Cryptocurrency_Conversions",
];

const sortFieldOrderWithTemplate = (fieldOrder = [], template = []) => {
    const templateSet = new Set(template);
    return [
        ...template.filter((field) => fieldOrder.includes(field)),
        ...fieldOrder.filter((field) => !templateSet.has(field)),
    ];
};

const AccountActivityV2 = ({
    asLabel,
    data,
    handleUpdate,
    hasError,
    setHasError,
    loading,
    cyclosFields,
    errors,
    setErrors,
    iPlusFields,
    wizard,
    handleChange,
    handlePrev,
    applicationForm,
    pageNum,
    variant,
    stepCount,
    stepLabel,
}) => {
    const isDocFirst = variant === VARIANT.V2;
    const { showError } = useContext(DialogContext);
    const visibleFieldOrder = useMemo(() => getVisibleWizardFieldOrder({
        fields: iPlusFields,
        wizard,
        data,
        type: Number(applicationForm?.type),
    }), [applicationForm?.type, data, iPlusFields, wizard]);
    const narrativeFieldOrder = useMemo(
        () => sortFieldOrderWithTemplate(
            visibleFieldOrder.filter((field) => PRIME_ACTIVITY_NARRATIVE_FIELDS.includes(field)),
            PRIME_ACTIVITY_NARRATIVE_FIELDS,
        ),
        [visibleFieldOrder],
    );
    const supportingFieldOrder = useMemo(
        () => sortFieldOrderWithTemplate(
            visibleFieldOrder.filter((field) => (
                !PRIME_ACTIVITY_NARRATIVE_FIELDS.includes(field)
                && !PRIME_ACTIVITY_TABLE_FIELDS.includes(field)
                && !PRIME_ACTIVITY_CONVERSION_FIELDS.includes(field)
                && !PRIME_ACTIVITY_COUNTRY_FIELDS.includes(field)
            )),
            PRIME_ACTIVITY_SUPPORTING_FIELDS,
        ),
        [visibleFieldOrder],
    );
    const conversionFieldOrder = useMemo(
        () => sortFieldOrderWithTemplate(
            visibleFieldOrder.filter((field) => PRIME_ACTIVITY_CONVERSION_FIELDS.includes(field)),
            PRIME_ACTIVITY_CONVERSION_FIELDS,
        ),
        [visibleFieldOrder],
    );
    const countryFieldOrder = useMemo(
        () => sortFieldOrderWithTemplate(
            visibleFieldOrder.filter((field) => PRIME_ACTIVITY_COUNTRY_FIELDS.includes(field)),
            PRIME_ACTIVITY_COUNTRY_FIELDS,
        ),
        [visibleFieldOrder],
    );
    const tableFieldOrder = useMemo(
        () => sortFieldOrderWithTemplate(
            visibleFieldOrder.filter((field) => PRIME_ACTIVITY_TABLE_FIELDS.includes(field)),
            PRIME_ACTIVITY_TABLE_FIELDS,
        ),
        [visibleFieldOrder],
    );
    const renderedFieldOrder = useMemo(() => [
        ...narrativeFieldOrder,
        ...supportingFieldOrder,
        ...conversionFieldOrder,
        ...countryFieldOrder,
        ...tableFieldOrder,
    ], [countryFieldOrder, conversionFieldOrder, narrativeFieldOrder, supportingFieldOrder, tableFieldOrder]);
    const handleFieldError = (field, errorMsg) => {
        setErrors((prevErrors) => ({ ...(prevErrors || {}), [field]: errorMsg }));
    };
    const supportingFieldMap = useMemo(() => ({
        ...(cyclosFields || {}),
        ...Object.fromEntries(
            [...STACKED_CHECKBOX_FIELDS]
                .filter((field) => cyclosFields?.[field])
                .map((field) => [field, {
                    ...cyclosFields[field],
                    uiClassName: 'col-12',
                    optionClassName: 'col-12',
                }])
        ),
    }), [cyclosFields]);
    const countryFieldMap = useMemo(() => ({
        ...(cyclosFields || {}),
        ...Object.fromEntries(
            countryFieldOrder
                .filter((field) => STACKED_CHECKBOX_FIELDS.has(field) && cyclosFields?.[field])
                .map((field) => [field, {
                    ...cyclosFields[field],
                    uiClassName: 'col-12',
                    // optionClassName: 'col-12',
                }])
        ),
    }), [countryFieldOrder, cyclosFields]);

    const checkNext = () => {
        const { fieldErrors } = collectVisibleFieldErrors({
            data,
            step: { wizard },
            iPlusFields,
            errors,
            type: Number(applicationForm?.type),
        });
        const { mergedErrors } = mergeClearedFieldErrors(errors, visibleFieldOrder, fieldErrors);
        setErrors(mergedErrors);

        const firstError = getFirstErrorInFieldOrder(renderedFieldOrder, mergedErrors);
        if (firstError?.message) {
            setHasError(true);
            showError(firstError.message);
            return false;
        }

        setErrors((prevErrors) => omitFieldPathErrors(prevErrors || {}, visibleFieldOrder));
        return true;
    };

    const handleSubmit = ({ isLastStep } = {}) => {
        if (!checkNext()) return;

        const crossFieldErrors = getAccountActivityRuleErrors(data, {
            depositLocationFields: ["Deposit_Locations"],
            paymentLocationFields: ["Payment_Locations"],
            skipWhenInterestedInPrime: false,
            deferLocationErrorsUntilActivityFieldsFilled: true,
        });

        if (crossFieldErrors.length) {
            setHasError(true);
            const mappedErrors = mapRuleErrorsToFieldErrors(crossFieldErrors);
            const orderedMappedErrors = mapRuleErrorsToFieldErrors(crossFieldErrors, { prioritizeActivityFields: true });
            const { mergedErrors } = mergeClearedFieldErrors(errors, visibleFieldOrder, mappedErrors);
            setErrors(mergedErrors);
            const firstErrorMessage = getFirstErrorInFieldOrder(renderedFieldOrder, {
                ...mergedErrors,
                ...orderedMappedErrors,
            })?.message || crossFieldErrors[0]?.message;
            if (firstErrorMessage) {
                showError(firstErrorMessage);
            }
            return;
        }

        setHasError(false);
        const nextStep = applicationForm?.getNextStepForForm?.({
            wizard,
            applicationForm,
            data,
        });
        return handleUpdate({ nextStep, isLastStep });
    };

    return (
        <div className='business-v2-step-shell' data-testid='business-v2-account-activity-step'>
            {isDocFirst ? (
                <DocumentFirstStepHead
                    stepIndex={pageNum}
                    stepCount={stepCount}
                    stepLabel={stepLabel}
                    title={stepLabel || 'Business activity and account usage'}
                    intro='How the account will be used, and the volumes you expect.'
                    dataTestId='business-v2-account-activity-head'
                />
            ) : (
                <div className='business-v2-step-header'>
                    <h2 className='business-v2-step-title'>Business Activity and Account Usage</h2>
                </div>
            )}

            <BusinessV2FieldGrid
                variant={variant}
                fieldOrder={narrativeFieldOrder}
                fieldMap={cyclosFields}
                data={data}
                asLabel={asLabel}
                handleChange={handleChange}
                hasError={hasError}
                onFieldError={handleFieldError}
                dataTestId='business-v2-account-activity-narrative-fields'
            />

            <BusinessV2FieldGrid
                variant={variant}
                fieldOrder={supportingFieldOrder}
                fieldMap={supportingFieldMap}
                data={data}
                asLabel={asLabel}
                handleChange={handleChange}
                hasError={hasError}
                onFieldError={handleFieldError}
                dataTestId='business-v2-account-activity-fields'
            />

            <BusinessV2FieldGrid
                variant={variant}
                fieldOrder={countryFieldOrder}
                fieldMap={countryFieldMap}
                data={data}
                asLabel={asLabel}
                handleChange={handleChange}
                hasError={hasError}
                onFieldError={handleFieldError}
                dataTestId='business-v2-account-activity-country-fields'
            />

            <BusinessV2FieldGrid
                variant={variant}
                fieldOrder={conversionFieldOrder}
                fieldMap={cyclosFields}
                data={data}
                asLabel={asLabel}
                handleChange={handleChange}
                hasError={hasError}
                onFieldError={handleFieldError}
                dataTestId='business-v2-account-activity-conversion-fields'
            />

            <ExpectedActivityTableV2
                fieldOrder={tableFieldOrder}
                fieldMap={cyclosFields}
                data={data}
                asLabel={asLabel}
                handleChange={handleChange}
                hasError={hasError}
                onFieldError={handleFieldError}
                dataTestId='business-v2-account-activity-transaction-table'
            />

            <div className='business-v2-step-actions'>
                <ButtonGroupNew
                        nextText={isDocFirst ? "Continue" : undefined}
                    onNextClick={handleSubmit}
                    onBackClick={() => handlePrev?.()}
                    showPageNum={false}
                    pageNum={pageNum}
                    asLabel={false}
                    loading={loading}
                    step={pageNum}
                    isDisabled={asLabel}
                />
            </div>
        </div>
    );
};

export default observer(AccountActivityV2);
